import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '../types/types'; // Updated import path
import { UserRole } from '../types/enums'; // Import UserRole from the enums file
import { loginApi, registerApi } from '../api/authApi';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  token: string | null;
  refreshToken: string | null; // Added refresh token
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    username: string,
    organizationAction: 'create' | 'join',
    organizationName?: string,
    organizationSlug?: string,
    inviteCode?: string,
  ) => Promise<void>; // Updated register signature
  logout: () => void;
  setLoading: (loading: boolean) => void;
  initializeAuth: () => void;
  setUser: (user: User) => void; // New action to directly set user
  setToken: (token: string, refreshToken: string | null) => void; // Action to set new tokens
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      isAuthenticated: false,
      isLoading: true,
      user: null,
      token: null,
      refreshToken: null, // Initialize refreshToken

      /** 🔐 LOGIN */
      login: async (email, password) => {
        set({ isLoading: true });
        try {
          const data = await loginApi(email, password);
          set({
            isAuthenticated: true,
            user: data.user, // The backend now sends the full user object including organizationId and permissions
            token: data.accessToken,
            refreshToken: data.refreshToken, // Assuming backend provides a refreshToken
            isLoading: false,
          });
        } catch (error) {
          console.error('Login failed:', error);
          set({ isAuthenticated: false, user: null, token: null, refreshToken: null, isLoading: false });
          throw error;
        }
      },

      /** 🧾 REGISTER */
      register: async (email, password, username, organizationAction, organizationName, organizationSlug, inviteCode) => {
        set({ isLoading: true });
        try {
          const data = await registerApi(
            email,
            password,
            username,
            organizationAction,
            organizationName,
            organizationSlug,
            inviteCode
          );
          set({
            isAuthenticated: true,
            user: data.user, // The backend now sends the full user object including organizationId and permissions
            token: data.accessToken,
            refreshToken: data.refreshToken, // Assuming backend provides a refreshToken
            isLoading: false,
          });
        } catch (error) {
          console.error('Registration failed:', error);
          set({ isAuthenticated: false, user: null, token: null, refreshToken: null, isLoading: false });
          throw error;
        }
      },

      /** 🚪 LOGOUT */
      logout: () => {
        set({
          isAuthenticated: false,
          user: null,
          token: null,
          refreshToken: null,
        });
      },

      /** ⏳ SET LOADING */
      setLoading: (loading) => set({ isLoading: loading }),

      /** ⚙️ SET USER DIRECTLY (useful after org/sub updates) */
      setUser: (user) => {
        set({ user });
      },

      /** 🔄 SET NEW TOKENS (after refresh) */
      setToken: (token, refreshToken = null) => {
        set({ token, refreshToken });
      },

      /** 🧠 INIT AUTH FROM STORAGE */
      initializeAuth: () => {
        const { token, refreshToken, user } = get();
        if (token && user) {
          // Additional validation for user object consistency if needed
          set({ isAuthenticated: true, isLoading: false });
        } else {
          set({ isLoading: false });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken, // Persist refresh token
      }),
    }
  )
);
